import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const Success = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const maxRetries = 10;
        const retryDelay = 1000;

        const fetchSessionData = async () => {
            try {
                const params = new URLSearchParams(location.search);
                const sessionId = params.get('session_id');
                
                if (!sessionId) {
                    console.error('No session ID found in URL');
                    setError('No session ID found');
                    setLoading(false);
                    return;
                }

                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                const response = await fetch(`${apiUrl}/api/stripe/session/${sessionId}`, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                console.log('Session response status:', response.status);
                
                const data = await response.json();
                console.log('Session data:', data);

                if (response.ok && data.email) {
                    setEmail(data.email);
                    setLoading(false);
                } else if (retryCount < maxRetries) {
                    console.log(`Retry attempt ${retryCount + 1} of ${maxRetries}`);
                    setTimeout(() => {
                        setRetryCount(prev => prev + 1);
                    }, retryDelay);
                } else {
                    setError('Failed to load session data after multiple attempts');
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error fetching session data:', error);
                setError('Error loading session data: ' + (error.message || 'Unknown error'));
                setLoading(false);
            }
        };

        fetchSessionData();
    }, [location, retryCount]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        
        // Client-side validation
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setSubmitting(false);
            return; // Don't proceed with API call
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            setSubmitting(false);
            return; // Don't proceed with API call
        }

        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/auth/complete-registration`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    password
                }),
            });
            
            const data = await response.json();
            console.log('Registration response:', data);

            if (response.ok) {
                localStorage.setItem('userToken', data.token);
                navigate('/dashboard');
            } else {
                // Server returned an error
                setError(data.message || 'Registration failed');
                setSubmitting(false);
            }
        } catch (error) {
            console.error('Registration error:', error);
            setError('Error completing registration: ' + (error.message || 'Unknown error'));
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-lg">
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <p className="ml-3 text-gray-600">
                        {retryCount > 0 ? `Setting up your account... (Attempt ${retryCount})` : 'Setting up your account...'}
                    </p>
                </div>
            </div>
        );
    }

    // Special case for serious errors (like no session ID)
    if (error && !email) {
        return (
            <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-lg">
                <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded">
                    <p className="text-red-700">{error}</p>
                </div>
                <button
                    onClick={() => navigate('/')}
                    className="mt-4 w-full bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                    Return to Homepage
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold mb-6 text-center">Complete Your Registration</h2>
            <div className="mb-6 p-4 bg-green-50 rounded-lg">
                <p className="text-green-800">Payment successful! Create a password to access your dashboard.</p>
            </div>

            {/* Show validation errors inline */}
            {error && (
                <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                        type="email"
                        value={email}
                        disabled
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`mt-1 block w-full p-2 border rounded-md ${
                            error && (password !== confirmPassword || password.length < 8)
                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                        required
                        minLength={8}
                        placeholder="At least 8 characters"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                    <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`mt-1 block w-full p-2 border rounded-md ${
                            error && password !== confirmPassword
                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                        required
                        minLength={8}
                        placeholder="Confirm your password"
                    />
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    className={`w-full p-2 rounded-md text-white ${
                        submitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                    } transition-colors`}
                >
                    {submitting ? 'Processing...' : 'Complete Registration'}
                </button>
                
                <div className="text-center mt-4">
                    <button
                        type="button"
                        onClick={() => navigate('/')}
                        className="text-blue-600 hover:underline"
                    >
                        Return to Homepage
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Success;